import { useState } from 'react';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import isDate from 'date-fns/isValid';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import HelpIcon from '@material-ui/icons/InfoOutlined';
import Link from 'components/CncUI/Link';
import Typography from 'components/CncUI/Typography';
import Dropdown from 'components/CncUI/Dropdown';
import TextField from 'components/CncUI/TextField';
import { TextFieldPhone } from 'components/CncUI/TextField';
import Button from 'components/CncUI/Button';
import Tooltip from 'components/CncUI/Tooltip';

import FmsEnums from 'constants/componentData/fmsEnums';

const useStyles = makeStyles(({ palette }) => ({
  help: {
    fontSize: 16,
    marginBottom: -2,
  },
  actions: {
    marginTop: 24,
    marginBottom: -16,
  },
  disabled: {
    color: palette.cncUI.primary.gray['+7'],
  },
}));

const EditForm = props => {
  const classes = useStyles();
  const { info, email, phone, mappings } = props;
  const { onCancel, onSave } = props;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    gender: info.gender || '',
    ethnicity: info.ethnicity || '',
    phone: phone.cell || '',
  });
  const formatDate = date => {
    if (!date) return date;
    const parsed = parse(date, 'yyyy-MM-dd', new Date());
    if (!isDate(parsed)) return date;
    return format(parsed, 'MM/dd/yyyy');
  };

  const handleChange = event => {
    const {
      target: { name, value },
    } = event;
    setForm({ ...form, [name]: value });
  };

  const valid = () => {
    const validPhone =
      form.phone && form.phone.replace(/\D/g, '').length === 10;
    return form.gender && form.ethnicity && validPhone;
  };

  const save = () => {
    setLoading(true);
    onSave({
      basicInfo: {
        gender: parseInt(form.gender, 10),
        ethnicity: parseInt(form.ethnicity, 10),
        dbCode: info.dbCode,
      },
      phoneNumbers: [
        {
          typeofContact: FmsEnums.typeofContact.primary,
          cell: form.phone.replace('+1', '').replace(/\D/g, ''),
        },
      ],
    });
  };

  const dobMonth = new Date(formatDate(info.birthDate)).getMonth() + 1;
  const dobDay = new Date(formatDate(info.birthDate)).getDate();
  const dobYear = new Date(formatDate(info.birthDate)).getFullYear();

  const message = `Please choose from the following options.
    You may opt out by selecting "Choose Not to Disclose".`;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8} style={{ marginTop: -16 }}>
        <Typography italic variant="body2" size="long">
          Certain fields below cannot be edited online for your security. To
          make changes to any of the disabled fields below, please contact our
          Employee Helpdesk at <strong>818.860.7756</strong> or{' '}
          <Link href="mailto:employeehelpdesk@castandcrew.com">
            employeehelpdesk@castandcrew.com
          </Link>{' '}
          to provide documentation to update this field.
        </Typography>
        <Typography variant="body2" size="short">
          *Denotes required field
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <Grid container spacing={2} style={{ flexGrow: 1 }}>
          <Grid item md={9} xs={7}>
            <TextField
              disabled
              fullWidth
              label="First Name"
              value={info.firstName}
            />
          </Grid>
          <Grid item md={3} xs={5}>
            <TextField
              disabled
              fullWidth
              label="Middle Initial"
              value={info.middleInitial}
            />
          </Grid>
          <Grid item md={9} xs={12}>
            <TextField
              disabled
              fullWidth
              label="Last Name"
              value={info.lastName}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={8}
            style={{ marginTop: -6, paddingTop: 0, paddingBottom: 16 }}
          >
            <Typography className={classes.disabled}>Date of Birth</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  value={`${dobMonth}`.length === 1 ? `0${dobMonth}` : dobMonth}
                  disabled
                  label="Month"
                  id={`EditForm-DobMonth`}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  value={`${dobDay}`.length === 1 ? `0${dobDay}` : dobDay}
                  disabled
                  label="Day"
                  id={`EditForm-DobDay`}
                />
              </Grid>
              <Grid item xs={6} sm={5}>
                <TextField
                  value={dobYear}
                  disabled
                  label="Year"
                  id={`EditForm-DobYear`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label={
                (info.idType || '').toUpperCase() === 'SIN'
                  ? 'Social Insurance Number'
                  : 'Social Security Number'
              }
              value={info.socialSecurityMask}
            />
          </Grid>

          <Grid item xs={11}>
            <Dropdown
              required
              fullWidth
              name="gender"
              label={
                <Typography component="span" style={{ color: 'inherit' }}>
                  Gender{' '}
                </Typography>
              }
              value={form.gender}
              onChange={handleChange}
              data={mappings.gender}
            />
          </Grid>
          <Grid
            item
            xs={1}
            style={{ display: 'flex', alignItems: 'center', marginTop: 12 }}
          >
            <Tooltip content={message} size="medium" position="top-left">
              <HelpIcon className={classes.help} />
            </Tooltip>
          </Grid>
          <Grid item xs={11}>
            <Dropdown
              required
              fullWidth
              name="ethnicity"
              label={
                <Typography component="span" style={{ color: 'inherit' }}>
                  Ethnicity{' '}
                </Typography>
              }
              value={form.ethnicity}
              onChange={handleChange}
              data={mappings.ethnicity}
            />
          </Grid>
          <Grid
            item
            xs={1}
            style={{ display: 'flex', alignItems: 'center', marginTop: 12 }}
          >
            <Tooltip content={message} size="medium" position="top-left">
              <HelpIcon className={classes.help} />
            </Tooltip>
          </Grid>

          <Grid item xs={12}>
            <TextField disabled fullWidth label="Email" value={email} />
          </Grid>
          <Grid item xs={12}>
            <TextFieldPhone
              required
              fullWidth
              label="Phone Number"
              name="phone"
              onChange={handleChange}
              value={form.phone}
              inputProps={{
                pattern: '[0-9]*',
                inputMode: 'numeric',
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={8} className={classes.actions}>
        <Grid
          container
          spacing={2}
          direction="row"
          justify="flex-end"
          alignItems="center"
        >
          <Grid item>
            <Button
              secondary
              disabled={loading}
              onClick={onCancel}
              id="BasicInfo-CancelButton"
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              loading={loading}
              onClick={save}
              disabled={!valid()}
              id="BasicInfo-SaveButton"
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EditForm;
